import React from 'react';
import { motion } from 'framer-motion';
import { Target, Award, Eye } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFirestoreDoc } from '../../hooks/useFirestoreDoc';
import type { SiteSettings } from '../../types/firebase';

const FALLBACK_STATS_NUMBERS = [
  { number: '10+', labelKey: 'common.stats.yearsExperience', delay: 0 },
  { number: '5000+', labelKey: 'common.stats.vehiclesServed', delay: 0.2 },
  { number: '98%', labelKey: 'common.stats.satisfaction', delay: 0.4 },
];

const ValuesSection: React.FC = () => {
  const { t } = useLanguage();

  const { data: settings } = useFirestoreDoc<SiteSettings>({
    collection: 'site-settings',
    id: 'general',
  });

  const values = [
    {
      id: 'precision',
      icon: Target,
      title: t('home.values.precision.title'),
      description: t('home.values.precision.description'),
      color: 'from-red-500 to-pink-500'
    },
    {
      id: 'expertise',
      icon: Award,
      title: t('home.values.expertise.title'),
      description: t('home.values.expertise.description'),
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'attention',
      icon: Eye,
      title: t('home.values.attention.title'),
      description: t('home.values.attention.description'),
      color: 'from-green-500 to-emerald-500'
    }
  ];

  return (
    <section className="py-20 bg-black text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t('home.values.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-500 mx-auto rounded-full" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {values.map((value, index) => {
            const IconComponent = value.icon;
            
            return (
              <motion.div
                key={value.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative text-center group"
              >
                {/* Animated Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Icon Container */}
                <motion.div
                  whileHover={{ scale: 1.1, rotateY: 180 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="relative z-10 mb-8"
                >
                  <div className={`w-24 h-24 mx-auto rounded-2xl bg-gradient-to-r ${value.color} flex items-center justify-center shadow-2xl`}>
                    <motion.div
                      animate={{ 
                        rotate: [0, 5, -5, 0],
                        scale: [1, 1.05, 1]
                      }}
                      transition={{ 
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <IconComponent className="w-12 h-12 text-white" />
                    </motion.div>
                  </div>
                  
                  {/* Floating particles */}
                  <motion.div
                    animate={{ 
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                      y: [0, -20, -40]
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeOut",
                      delay: index * 0.5
                    }}
                    className="absolute top-0 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full"
                  />
                </motion.div>

                {/* Content */}
                <div className="relative z-10 px-4">
                  <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-red-400 transition-colors duration-300">
                    {value.title}
                  </h3>
                  
                  <p className="text-gray-300 leading-relaxed group-hover:text-white transition-colors duration-300">
                    {value.description}
                  </p>
                </div>

                {/* Decorative Elements */}
                <motion.div
                  animate={{ 
                    rotate: [0, 360],
                    scale: [1, 1.2, 1]
                  }}
                  transition={{ 
                    duration: 8,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  className="absolute -top-4 -right-4 w-8 h-8 border-2 border-red-600/30 rounded-full opacity-50"
                />
                
                <motion.div
                  animate={{ 
                    rotate: [360, 0],
                    scale: [1.2, 1, 1.2]
                  }}
                  transition={{ 
                    duration: 6,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  className="absolute -bottom-4 -left-4 w-6 h-6 border border-red-600/20 rounded-full opacity-30"
                />
              </motion.div>
            );
          })}
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 text-center"
        >
          {(settings?.stats
            ? [
                { number: settings.stats.yearsExperience, labelKey: 'common.stats.yearsExperience', delay: 0 },
                { number: settings.stats.vehiclesServed, labelKey: 'common.stats.vehiclesServed', delay: 0.2 },
                { number: settings.stats.satisfaction, labelKey: 'common.stats.satisfaction', delay: 0.4 },
              ]
            : FALLBACK_STATS_NUMBERS
          ).map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: stat.delay }}
              className="group"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="p-8 rounded-2xl bg-gradient-to-br from-red-600/20 to-transparent border border-red-600/30 hover:border-red-600/50 transition-all duration-300"
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: stat.delay + 0.3 }}
                  className="text-4xl md:text-5xl font-bold text-red-500 mb-2"
                >
                  <motion.span
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 1, delay: stat.delay + 0.5 }}
                  >
                    {stat.number}
                  </motion.span>
                </motion.div>
                <p className="text-gray-300 font-medium">
                  {t(stat.labelKey)}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ValuesSection;